/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

/* Importing the components from the material-ui library. */
import { Breadcrumbs, Button, Container, Grid, Stack, Typography } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';

/* Importing the functions from the Redux slices. */
import { searchDevice } from '../../features/device/deviceSlice';

/* Importing components from the components folder. */
import MaterialTable from '../../components/MaterialTable';
import MonitorEdit from '../../sections/monitor/monitor.section.edit';
import Page from '../../components/Page';

/* Importing function from the utils folder. */
import { fDevices } from '../../utils/Device/Device.utils.format';

/**
 * Renders the Monitoring Dashboard List page.
 * @returns {JSX.Element} The Monitoring List page component.
 */
export default function MonitoringList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* Getting the state from the redux store. */
  const { searchResults } = useSelector((state) => state.device);

  /* Setting the initial state of the component. */
  const [title] = useState('Monitoring Dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [Devices, setDevices] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deviceID, setDeviceID] = useState();

  const updateDeviceHandler = (id) => {
    setDeviceID(id);
    setIsEditOpen(true);
  };

  useEffect(() => {
    const devicesArray = fDevices(searchResults.map((p) => p.resource));
    setDevices(devicesArray);
    setIsLoading(false);
  }, [searchResults]);

  /**
   * Initializes the data for the page.
   */
  const initializeData = useCallback(() => {
    dispatch(searchDevice({ query: `type:text=Lifeline Monitor` }));
  }, [dispatch]);

  /* Calling the initializeData function. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  /**
   * The columns for the table that displays the devices.
   */
  const columns = [
    {
      title: 'Name',
      field: 'deviceName_name',
    },
  ];

  /**
   * Renders the table
   */
  const renderTable = () => (
    <MaterialTable
      onRowClick={(event, rowData) => {
        navigate(`/monitoring/dashboard/${rowData.id}`, { replace: false });
      }}
      options={{
        search: true,
        actionsColumnIndex: -1,
      }}
      actions={[
        {
          icon: () => <EditIcon />,
          tooltip: 'Edit Dashboard',
          onClick: (event, rowData) => updateDeviceHandler(rowData.id),
        },
      ]}
      title={''}
      isLoading={isLoading}
      columns={columns}
      data={Devices}
    />
  );

  return (
    <Page title={title}>
      <Container maxWidth="none">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>
              {title}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" mr={4}>
              <Typography color="text.primary">Monitors</Typography>
            </Breadcrumbs>
          </Stack>

          <Button variant="contained" component={RouterLink} to="/device/add" startIcon={<AddIcon />}>
            Add Lifeline Monitor
          </Button>
        </Stack>

        <Grid container>
          <Grid item xs={12}>
            {renderTable()}
          </Grid>
        </Grid>
        <MonitorEdit isEditOpen={isEditOpen} deviceID={deviceID} setIsEditOpen={setIsEditOpen} />
      </Container>
    </Page>
  );
}
